(() => {
  const root = document.querySelector('[data-seras-pc]');
  if (!root) return;

  const stage = root.querySelector('.spc__stage');
  const intro = root.querySelector('.spc__intro');
  const head = root.querySelector('.spc__head');
  const foot = root.querySelector('.spc__foot');
  const field = root.querySelector('.spc__field');
  const cam = root.querySelector('.spc__cam');
  const canvas = root.querySelector('canvas.spc__map');
  const ctx = canvas.getContext('2d');
  const recs = [...root.querySelectorAll('.sp-rec')].map((el) => ({
    el,
    icon: el.querySelector('svg'),
    label: el.querySelector('.sp-rec__label'),
  }));

  const still = window.matchMedia('(prefers-reduced-motion: reduce), (max-height: 559px)');
  const mobile = window.matchMedia('(max-width: 989px)');

  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const ease = (v) => v * v * (3 - 2 * v);
  const part = (v, from, to) => clamp((v - from) / (to - from));

  // Niveles de concentración, del más externo al núcleo. Colores sobre tinta.
  const FILL = [0.05, 0.06, 0.07, 0.09, 0.13, 0.18, 0.3];
  const LINE = [0.2, 0.24, 0.28, 0.34, 0.42, 0.52, 0.7];

  let levels = [];
  let sources = [];
  let paths = [];
  let vbW = 1440;
  let vbH = 560;
  let W = 0;
  let H = 0;
  let dpr = 1;
  const mask = document.createElement('canvas');
  const mctx = mask.getContext('2d');
  const tmp = document.createElement('canvas');
  const tctx = tmp.getContext('2d');
  const MS = 4; // la máscara va a un cuarto de resolución: el borde queda suave y cuesta poco

  const build = () => {
    if (!paths.length) return;
    const pixel = Math.min(window.devicePixelRatio || 1, 1.5);
    W = field.offsetWidth;
    H = field.offsetHeight;
    dpr = pixel;
    canvas.width = tmp.width = Math.round(W * dpr);
    canvas.height = tmp.height = Math.round(H * dpr);
    mask.width = Math.ceil(canvas.width / MS);
    mask.height = Math.ceil(canvas.height / MS);
    const matrix = new DOMMatrix([(W * dpr) / vbW, 0, 0, (H * dpr) / vbH, 0, 0]);
    levels = paths.map((source, i) => {
      const level = document.createElement('canvas');
      level.width = canvas.width;
      level.height = canvas.height;
      const c = level.getContext('2d');
      const path = new Path2D();
      path.addPath(source, matrix);
      c.fillStyle = `rgba(34, 191, 176, ${FILL[i]})`;
      c.fill(path, 'evenodd');
      c.strokeStyle = `rgba(150, 232, 221, ${LINE[i]})`;
      c.lineWidth = dpr;
      c.stroke(path);
      return level;
    });
    const narrow = mobile.matches;
    sources = recs.map((rec, k) => {
      const css = rec.el.style;
      const x = narrow ? (css.getPropertyValue('--mx') / 100) * W : (css.getPropertyValue('--x') / 1440) * W;
      const y = narrow ? (css.getPropertyValue('--my') / 100) * H : (css.getPropertyValue('--y') / 560) * H;
      return { x: x + 4, y: y + 4, delay: k * 0.035 };
    });
    last = -1;
  };

  // Cada nivel avanza desde los receptores: primero el núcleo y al final el borde de la pluma
  const draw = (t) => {
    if (!levels.length) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const reach = Math.max(W, H) * 1.5;
    const feather = reach * 0.1;
    const count = levels.length;
    levels.forEach((level, i) => {
      const start = ((count - 1 - i) / (count - 1)) * 0.4;
      if (t >= 1) {
        ctx.drawImage(level, 0, 0);
        return;
      }
      if (t <= start) return;
      mctx.setTransform(1, 0, 0, 1, 0, 0);
      mctx.clearRect(0, 0, mask.width, mask.height);
      mctx.globalCompositeOperation = 'lighter';
      const unit = dpr / MS;
      sources.forEach((source) => {
        const local = part(t, start + source.delay, 1);
        if (local <= 0) return;
        const radius = ease(local) * reach + 1;
        // El viento estira la pluma a lo ancho
        mctx.setTransform(unit * 1.6, 0, 0, unit, source.x * unit, source.y * unit);
        const gradient = mctx.createRadialGradient(0, 0, 0, 0, 0, radius);
        gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
        gradient.addColorStop(clamp((radius - feather) / radius), 'rgba(0, 0, 0, 1)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        mctx.fillStyle = gradient;
        mctx.beginPath();
        mctx.arc(0, 0, radius, 0, Math.PI * 2);
        mctx.fill();
      });
      tctx.globalCompositeOperation = 'source-over';
      tctx.clearRect(0, 0, tmp.width, tmp.height);
      tctx.drawImage(level, 0, 0);
      tctx.globalCompositeOperation = 'destination-in';
      tctx.drawImage(mask, 0, 0, tmp.width, tmp.height);
      ctx.drawImage(tmp, 0, 0);
    });
  };

  const paint = (p, t) => {
    const narrow = mobile.matches;
    const open = ease(part(p, 0, 0.8));
    // Cámara: arranca corrida y cerca, y se abre al plano completo
    const shift = narrow ? 0 : (1 - open) * 34;
    const zoom = narrow ? 1 : 1.1 - open * 0.1;
    cam.style.transform = `translateX(${shift.toFixed(2)}%) scale(${zoom.toFixed(4)})`;

    const gone = part(p, 0.04, 0.42);
    intro.style.opacity = gone > 0 ? (1 - gone).toFixed(3) : '';
    intro.style.transform = gone > 0 ? (narrow ? `translateY(${(gone * 36).toFixed(1)}px)` : `translateX(${(gone * -48).toFixed(1)}px)`) : '';
    intro.style.visibility = gone >= 1 ? 'hidden' : '';

    const shown = part(p, 0.42, 0.78);
    head.style.opacity = foot.style.opacity = shown.toFixed(3);
    foot.style.visibility = head.style.visibility = shown <= 0 ? 'hidden' : 'visible';

    recs.forEach((rec, k) => {
      const label = part(p, 0.42 + k * 0.05, 0.62 + k * 0.05);
      rec.label.style.opacity = label.toFixed(3);
      rec.label.style.translate = `0 ${((1 - label) * 8).toFixed(1)}px`;
      rec.el.style.pointerEvents = label > 0.5 ? 'auto' : 'none';
      rec.icon.style.opacity = part(t, k * 0.035, k * 0.035 + 0.08).toFixed(3);
    });
  };

  const clear = () => {
    cam.style.transform = '';
    [intro, head, foot].forEach((el) => {
      el.style.opacity = el.style.transform = el.style.visibility = '';
    });
    recs.forEach((rec) => {
      rec.label.style.opacity = rec.label.style.translate = '';
      rec.el.style.pointerEvents = rec.icon.style.opacity = '';
    });
  };

  const progress = () => {
    const box = root.getBoundingClientRect();
    const run = box.height - stage.offsetHeight;
    if (run <= 0) return 0;
    const top = parseFloat(getComputedStyle(stage).top) || 0;
    return clamp((top - box.top) / run);
  };

  // En modo quieto el ancla va justo antes del plano; en película, al final del recorrido
  const anchor = root.querySelector('.spc__anchor');
  const placeAnchor = () => {
    if (still.matches) head.before(anchor);
    else root.prepend(anchor);
  };

  let smooth = 0;
  let last = -1;
  let clock = 0;
  let born = 0;
  let raf = 0;
  let visible = true;
  let painted = -1;

  const frame = (now) => {
    raf = 0;
    if (still.matches) return;
    const dt = Math.min(64, now - clock || 16);
    clock = now;
    const target = progress();
    smooth += (target - smooth) * (1 - Math.exp(-dt / 110));
    if (Math.abs(target - smooth) < 0.0002) smooth = target;
    const elapsed = born ? (now - born) / 3400 : 0;
    const t = clamp(0.36 * ease(clamp(elapsed)) + 0.64 * smooth);
    if (smooth !== painted || elapsed <= 1.02) {
      paint(smooth, t);
      painted = smooth;
    }
    if (last < 0 || Math.abs(t - last) > 0.0004) {
      draw(t);
      last = t;
    }
    // Sin nada que mover no se gasta procesador; el scroll lo vuelve a despertar
    if (visible && (smooth !== target || (born && elapsed <= 1.02))) raf = requestAnimationFrame(frame);
  };

  const run = () => {
    if (!raf && visible && !still.matches) {
      clock = performance.now();
      raf = requestAnimationFrame(frame);
    }
  };

  const settle = () => {
    placeAnchor();
    build();
    if (still.matches) {
      clear();
      draw(1);
    } else {
      painted = -1;
      run();
    }
  };

  const load = () => {
    const url = mobile.matches ? root.dataset.svgM : root.dataset.svg;
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const svg = new DOMParser().parseFromString(text, 'image/svg+xml');
        paths = [...svg.querySelectorAll('path')].map((path) => new Path2D(path.getAttribute('d')));
        vbW = mobile.matches ? 1000 : 1440;
        vbH = mobile.matches ? 920 : 560;
        build();
        born = performance.now();
        if (still.matches) draw(1);
        else run();
      })
      .catch(() => {
        /* se queda la tinta con los receptores */
      });
  };

  new IntersectionObserver((entries) => {
    visible = entries[0].isIntersecting;
    run();
  }).observe(root);

  let wait = 0;
  let wide = window.innerWidth;
  window.addEventListener('resize', () => {
    // En celular la barra del navegador cambia el alto al bajar; eso no redibuja
    if (window.innerWidth === wide && mobile.matches) return;
    wide = window.innerWidth;
    clearTimeout(wait);
    wait = setTimeout(settle, 160);
  });
  mobile.addEventListener('change', load);
  still.addEventListener('change', settle);

  window.addEventListener('scroll', run, { passive: true });

  placeAnchor();
  if (still.matches) clear();
  else if (progress() > 0) paint(progress(), 0);

  // El plano arranca cuando la página ya cargó, para no retrasar lo primero que se ve
  const start = () => ('requestIdleCallback' in window ? requestIdleCallback(load, { timeout: 1500 }) : setTimeout(load, 800));
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
})();
